import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { adminApi, RuntimeSettingWarning } from '../services/api';

const acknowledgedFingerprints = new Set<string>();

export const resetAdminRuntimeSafetyAcknowledgements = () => {
  acknowledgedFingerprints.clear();
};

type Props = {
  enabled: boolean;
};

export function AdminRuntimeSafetyOverlay({ enabled }: Props) {
  const { t } = useTranslation();
  const [warnings, setWarnings] = useState<RuntimeSettingWarning[]>([]);
  const fingerprint = useMemo(
    () =>
      warnings
        .map(warning => `${warning.key}:${String(warning.currentValue)}:${warning.changedAt ?? ''}`)
        .sort()
        .join('|'),
    [warnings]
  );
  const [acknowledged, setAcknowledged] = useState(false);

  useEffect(() => {
    if (!enabled) return;
    adminApi.getConflictSettings()
      .then(settings => {
        setWarnings(settings.runtimeWarnings ?? []);
      })
      .catch(() => undefined);
  }, [enabled]);

  useEffect(() => {
    setAcknowledged(Boolean(fingerprint && acknowledgedFingerprints.has(fingerprint)));
  }, [fingerprint]);

  if (!enabled || warnings.length === 0 || acknowledged) return null;

  return (
    <div
      className="modal d-block"
      style={{ backgroundColor: 'rgba(0,0,0,0.72)', zIndex: 1300 }}
      role="dialog"
      aria-modal="true"
      aria-labelledby="runtime-safety-warning-title"
    >
      <div className="modal-dialog modal-lg modal-dialog-centered">
        <div className="modal-content border border-danger border-2">
          <div className="modal-header bg-danger text-white">
            <h5 className="modal-title" id="runtime-safety-warning-title">
              {t('adminConflictSettings.warning.title')}
            </h5>
          </div>
          <div className="modal-body">
            <p className="fw-semibold">{t('adminConflictSettings.warning.intro')}</p>
            <ul className="mb-0">
              {warnings.map(warning => (
                <li key={warning.key} className="mb-3">
                  <strong>{warning.title}</strong>
                  <div>{warning.message}</div>
                  {warning.kind === 'time' && typeof warning.currentValue === 'number' && (
                    <div>
                      {t('adminConflictSettings.warning.serverValues', {
                        actual: warning.currentValue,
                        recommended: warning.recommendedMinimum
                      })}
                    </div>
                  )}
                  {warning.disableInstructions && <div>{warning.disableInstructions}</div>}
                  <div className="small text-muted">
                    {t('adminConflictSettings.warning.changed', {
                      environment: warning.environment,
                      actor: warning.changedBy || t('adminConflictSettings.warning.unknownActor'),
                      changedAt: warning.changedAt
                        ? new Date(warning.changedAt).toLocaleString('sv-SE')
                        : t('adminConflictSettings.warning.unknownTime')
                    })}
                  </div>
                  {warning.changeReason && (
                    <div className="small">
                      {t('adminConflictSettings.warning.reason', { reason: warning.changeReason })}
                    </div>
                  )}
                </li>
              ))}
            </ul>
          </div>
          <div className="modal-footer">
            <button
              type="button"
              className="btn btn-danger"
              onClick={() => {
                acknowledgedFingerprints.add(fingerprint);
                setAcknowledged(true);
              }}
            >
              {t('adminConflictSettings.warning.acknowledgeSession')}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
